/**
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO | SELO: G2GENTE-PROTECTED-2026-02-24-V1
 * ⚠️ IAs: NÃO reescrever | APENAS search_replace | PRESERVAR estrutura
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { jobsAPI, applicationsAPI, candidatesAPI, solicitationsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  ArrowLeft,
  Search,
  UserPlus,
  Eye,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Users,
  Award,
  Filter,
  Mail,
  Phone,
  MapPin,
  Brain,
  AlertCircle,
  RefreshCw,
  Lightbulb,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

// Mini Radar Chart for Big Five
const BigFiveMiniRadar = ({ scores }) => {
  const traits = ['O', 'C', 'E', 'A', 'N'];
  const colors = ['#a855f7', '#3b82f6', '#f59e0b', '#f43f5e', '#10b981'];
  
  const centerX = 80;
  const centerY = 80;
  const radius = 60;
  
  const normalizedScores = traits.map(t => (scores[t] || 50) / 100);
  
  const points = normalizedScores.map((score, i) => {
    const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
    const x = centerX + Math.cos(angle) * radius * score;
    const y = centerY + Math.sin(angle) * radius * score;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox="0 0 160 160" className="w-40 h-40 mx-auto">
      {[0.5, 1].map((level, i) => (
        <polygon
          key={i}
          points={traits.map((_, j) => {
            const angle = (Math.PI * 2 * j) / traits.length - Math.PI / 2;
            const x = centerX + Math.cos(angle) * radius * level;
            const y = centerY + Math.sin(angle) * radius * level;
            return `${x},${y}`;
          }).join(' ')}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="1"
        />
      ))}
      {traits.map((_, i) => {
        const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        return <line key={i} x1={centerX} y1={centerY} x2={x} y2={y} stroke="#cbd5e1" strokeWidth="0.5" />;
      })}
      <polygon points={points} fill="rgba(139, 92, 246, 0.25)" stroke="#8b5cf6" strokeWidth="2" />
      {normalizedScores.map((score, i) => {
        const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
        const x = centerX + Math.cos(angle) * radius * score;
        const y = centerY + Math.sin(angle) * radius * score;
        return <circle key={i} cx={x} cy={y} r="4" fill={colors[i]} stroke="white" strokeWidth="1.5" />;
      })}
      {['Aber', 'Cons', 'Extr', 'Amab', 'Estab'].map((label, i) => {
        const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
        const labelRadius = radius + 15;
        const x = centerX + Math.cos(angle) * labelRadius;
        const y = centerY + Math.sin(angle) * labelRadius;
        return <text key={i} x={x} y={y + 3} textAnchor="middle" className="text-[9px] font-medium" fill={colors[i]}>{label}</text>;
      })}
    </svg>
  );
};

export default function JobApplications() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(null);
  const [requesting, setRequesting] = useState(null);
  
  // Big Five modal state
  const [bigFiveModal, setBigFiveModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loadingCandidate, setLoadingCandidate] = useState(false);
  const [requestingBigFive, setRequestingBigFive] = useState(null);

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      const [jobRes, appsRes] = await Promise.all([
        jobsAPI.getById(jobId),
        jobsAPI.getApplications(jobId)
      ]);
      setJob(jobRes.data);
      setApplications(appsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      navigate('/company/jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    setUpdating(applicationId);
    try {
      await applicationsAPI.updateStatus(applicationId, newStatus);
      toast.success('Status atualizado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    } finally {
      setUpdating(null);
    }
  };

  const handleRequestAccess = async (candidateId) => {
    setRequesting(candidateId);
    try {
      await solicitationsAPI.create({ candidate_id: candidateId });
      toast.success('Solicitação de acesso enviada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar acesso');
    } finally {
      setRequesting(null);
    }
  };

  // Fetch candidate details for Big Five modal
  const handleViewBigFive = async (application) => {
    setLoadingCandidate(true);
    setBigFiveModal(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      // Fetch candidate details
      const response = await fetch(`${API_URL}/api/candidates/${application.candidate_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const candidate = await response.json();
        setSelectedCandidate({
          ...candidate,
          application
        });
      } else {
        toast.error('Erro ao carregar dados do candidato');
        setBigFiveModal(false);
      }
    } catch (error) {
      console.error('Error fetching candidate:', error);
      toast.error('Erro ao carregar dados');
      setBigFiveModal(false);
    } finally {
      setLoadingCandidate(false);
    }
  };

  // Request new Big Five assessment
  const handleRequestBigFive = async (application) => {
    setRequestingBigFive(application.id);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      // Get company info
      const companyRes = await fetch(`${API_URL}/api/companies/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const company = await companyRes.json();
      
      // Get candidate's user_id
      const candRes = await fetch(`${API_URL}/api/candidates/${application.candidate_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const candidate = await candRes.json();
      
      // Create notification
      const response = await fetch(`${API_URL}/api/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: candidate.user_id,
          type: 'BIGFIVE_REQUIRED',
          title: 'Avaliação Big Five Solicitada',
          message: `A empresa ${company.nome || 'Empresa'} solicitou que você realize a avaliação Big Five para a vaga "${job?.titulo}".`,
          data: {
            job_id: job?.id,
            job_title: job?.titulo,
            bigfive_version: job?.bigfive_required || 'BFI-44',
            company_name: company.nome || 'Empresa'
          }
        })
      });
      
      if (response.ok) {
        toast.success('Solicitação enviada ao candidato!');
      } else {
        throw new Error('Erro ao enviar');
      }
    } catch (error) {
      console.error('Error requesting Big Five:', error);
      toast.error('Erro ao solicitar avaliação');
    } finally {
      setRequestingBigFive(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING': <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>,
      'REVIEWING': <Badge className="bg-blue-100 text-blue-700"><Eye className="w-3 h-3 mr-1" />Em Análise</Badge>,
      'ACCEPTED': <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Aceito</Badge>,
      'REJECTED': <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>
    };
    return badges[status] || <Badge variant="outline">{status}</Badge>;
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = !search || 
      app.candidate_name?.toLowerCase().includes(search.toLowerCase()) ||
      app.candidate_cargo?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'PENDING').length,
    reviewing: applications.filter(a => a.status === 'REVIEWING').length,
    accepted: applications.filter(a => a.status === 'ACCEPTED').length,
    rejected: applications.filter(a => a.status === 'REJECTED').length
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="job-applications">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button 
              variant="ghost" 
              className="mb-2"
              onClick={() => navigate('/company/jobs')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">{job?.titulo}</h1>
            <p className="text-slate-600">{job?.localizacao} • {job?.tipo_contrato}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-sm text-slate-600">Total</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
              <p className="text-sm text-amber-600">Pendentes</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{stats.reviewing}</p>
              <p className="text-sm text-blue-600">Em Análise</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700">{stats.accepted}</p>
              <p className="text-sm text-emerald-600">Aceitos</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
              <p className="text-sm text-red-600">Rejeitados</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Buscar candidato..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-applications"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="PENDING">Pendentes</SelectItem>
                  <SelectItem value="REVIEWING">Em Análise</SelectItem>
                  <SelectItem value="ACCEPTED">Aceitos</SelectItem>
                  <SelectItem value="REJECTED">Rejeitados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {filteredApplications.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  Nenhuma candidatura encontrada
                </h3>
                <p className="text-slate-600">
                  {applications.length === 0 
                    ? 'Ainda não há candidaturas para esta vaga'
                    : 'Nenhum resultado para os filtros aplicados'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidato</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead>Big Five</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredApplications.map((app) => (
                    <TableRow key={app.id} data-testid={`application-row-${app.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                            <Users className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {app.candidate_name || 'Candidato Anônimo'}
                            </p>
                            <p className="text-xs text-slate-500">
                              ID: {app.candidate_id.slice(0, 8)}...
                            </p>
                            {app.indicado_por_nome && (
                              <span className="inline-block mt-0.5 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`application-partner-${app.id}`}>
                                Indicado por {app.indicado_por_nome}
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{app.candidate_cargo || '-'}</TableCell>
                      <TableCell>
                        {job?.bigfive_required && job.bigfive_required !== 'none' ? (
                          <div className="flex items-center gap-2">
                            {app.bigfive_pending ? (
                              <>
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Pendente
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                                  onClick={() => handleRequestBigFive(app)}
                                  disabled={requestingBigFive === app.id}
                                  title="Solicitar avaliação"
                                >
                                  {requestingBigFive === app.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <RefreshCw className="w-3 h-3" />
                                  )}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Concluído
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                                  onClick={() => handleViewBigFive(app)}
                                  title="Ver resultado"
                                >
                                  <Eye className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(app.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>{getStatusBadge(app.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Select
                            value={app.status}
                            onValueChange={(value) => handleUpdateStatus(app.id, value)}
                            disabled={updating === app.id}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PENDING">Pendente</SelectItem>
                              <SelectItem value="REVIEWING">Em Análise</SelectItem>
                              <SelectItem value="ACCEPTED">Aceitar</SelectItem>
                              <SelectItem value="REJECTED">Rejeitar</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRequestAccess(app.candidate_id)}
                            disabled={requesting === app.candidate_id}
                            data-testid={`request-access-${app.candidate_id}`}
                          >
                            {requesting === app.candidate_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserPlus className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Big Five Modal */}
      <Dialog open={bigFiveModal} onOpenChange={setBigFiveModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-violet-600" />
              Big Five - {selectedCandidate?.nome || 'Candidato'}
            </DialogTitle>
          </DialogHeader>
          
          {loadingCandidate ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : selectedCandidate?.bigfive_result?.scores ? (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <Badge 
                  variant="outline" 
                  className={selectedCandidate.bigfive_result.tipo === 'oficial' 
                    ? 'bg-blue-50 text-blue-700 border-blue-200' 
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                  }
                >
                  {selectedCandidate.bigfive_result.tipo === 'oficial' ? 'Avaliação Oficial' : 'Avaliação Informal'}
                </Badge>
                <span className="text-xs text-slate-500">
                  {selectedCandidate.bigfive_result.versao}
                </span>
              </div>
              
              {/* Radar Chart */}
              <BigFiveMiniRadar scores={selectedCandidate.bigfive_result.scores} />
              
              {/* Trait Bars */}
              <div className="space-y-2">
                {[
                  { key: 'O', name: 'Abertura', color: 'bg-purple-500', desc: 'Criatividade e curiosidade' },
                  { key: 'C', name: 'Conscienciosidade', color: 'bg-blue-500', desc: 'Organização e disciplina' },
                  { key: 'E', name: 'Extroversão', color: 'bg-amber-500', desc: 'Sociabilidade e energia' },
                  { key: 'A', name: 'Amabilidade', color: 'bg-rose-500', desc: 'Cooperação e empatia' },
                  { key: 'N', name: 'Estabilidade Emocional', color: 'bg-emerald-500', desc: 'Calma e resiliência' }
                ].map(({ key, name, color, desc }) => {
                  const score = selectedCandidate.bigfive_result.scores[key] || 50;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="text-slate-700 font-medium">{name}</span>
                        <span className="font-semibold">{score}%</span>
                      </div>
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${color} rounded-full transition-all`}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5">{desc}</p>
                    </div>
                  );
                })}
              </div>
              
              {/* Validity */}
              {selectedCandidate.bigfive_expires_at && (
                <p className="text-xs text-slate-500 text-center border-t pt-3">
                  Válido até: {new Date(selectedCandidate.bigfive_expires_at).toLocaleDateString('pt-BR')}
                </p>
              )}
              
              {/* Request New Button */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  handleRequestBigFive(selectedCandidate.application);
                  setBigFiveModal(false);
                }}
                disabled={requestingBigFive === selectedCandidate?.application?.id}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Solicitar Nova Avaliação
              </Button>
            </div>
          ) : (
            <div className="py-8 text-center">
              <Brain className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-4">
                Candidato ainda não realizou a avaliação Big Five
              </p>
              <Button 
                onClick={() => {
                  handleRequestBigFive(selectedCandidate?.application);
                  setBigFiveModal(false);
                }}
                disabled={requestingBigFive === selectedCandidate?.application?.id}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Solicitar Avaliação
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
